import { useEffect, useMemo, useState } from "react";
import { Modal, InputNumber, Table, Typography, Tag, message, Button, Space } from "antd";
import axiosInstance from "../../../../variables/api/axiosInstance";

const { Text } = Typography;
const fmt = (v) => (v || v === 0 ? Number(v).toLocaleString("fr-FR") : "0");

/**
 * Ventilation d'un règlement (décaissement) PAR GARANTIE (suivi par garantie — Phase 1).
 * La somme des montants doit égaler le montant global du décaissement.
 * props : open, onClose, onSaved, decaissement {codeId, montantGlobal}, sinistreCodeId
 */
const VentilationGarantieModal = ({ open, onClose, onSaved, decaissement, sinistreCodeId }) => {
    const [garanties, setGaranties] = useState([]); // [{garantieCodeId, garantieNom, provision, regle, sap}]
    const [montants, setMontants] = useState({});   // garantieCodeId -> montant
    const [saving, setSaving] = useState(false);
    const total = Number(decaissement?.montantGlobal) || 0;

    useEffect(() => {
        if (!open || !sinistreCodeId) return;
        axiosInstance.get(`/api/sinistre/sap-garanties/${sinistreCodeId}`)
            .then((r) => {
                const rows = r.data || [];
                setGaranties(rows);
                // Pré-remplissage : ventilation existante si présente, sinon vide.
                const init = {};
                (decaissement?.ventilation || []).forEach((v) => {
                    if (v?.garantieCodeId != null) init[v.garantieCodeId] = v.montant;
                });
                setMontants(init);
            })
            .catch(() => setGaranties([]));
    }, [open, sinistreCodeId, decaissement]);

    const somme = useMemo(
        () => Object.values(montants).reduce((a, v) => a + (Number(v) || 0), 0),
        [montants]
    );
    const reste = total - somme;

    const repartirAuProrataSap = () => {
        const totalSap = garanties.reduce((a, g) => a + (Number(g.sap) || 0), 0);
        const next = {};
        if (totalSap > 0) {
            garanties.forEach((g) => { next[g.garantieCodeId] = Math.round((Number(g.sap) || 0) / totalSap * total); });
            // Ajuste le résidu d'arrondi sur la plus grosse ligne.
            const s = Object.values(next).reduce((a, v) => a + v, 0);
            const diff = total - s;
            if (diff !== 0 && garanties.length) {
                const gmax = garanties.reduce((m, g) => ((next[g.garantieCodeId] || 0) > (next[m.garantieCodeId] || 0) ? g : m), garanties[0]);
                next[gmax.garantieCodeId] = (next[gmax.garantieCodeId] || 0) + diff;
            }
        }
        setMontants(next);
    };

    const enregistrer = () => {
        if (Math.round(somme) !== Math.round(total)) {
            message.warning(`La ventilation (${fmt(somme)}) doit être égale au montant du règlement (${fmt(total)}).`);
            return;
        }
        const lignes = garanties
            .map((g) => ({ garantieCodeId: g.garantieCodeId, montant: Number(montants[g.garantieCodeId]) || 0 }))
            .filter((l) => l.montant > 0);
        setSaving(true);
        axiosInstance.put(`/api/decaissement/${decaissement.codeId}/ventilation`, lignes)
            .then(() => { message.success("Règlement ventilé par garantie."); onSaved && onSaved(); onClose && onClose(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec de la ventilation."))
            .finally(() => setSaving(false));
    };

    return (
        <Modal open={open} onCancel={onClose} width={720} title="Ventiler le règlement par garantie"
            okText="Enregistrer" onOk={enregistrer} confirmLoading={saving}
            okButtonProps={{ disabled: Math.round(somme) !== Math.round(total) }}>
            <div style={{ marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <Text>Montant du règlement : <b>{fmt(total)} F</b></Text>
                <Space>
                    <Button size="small" onClick={repartirAuProrataSap}>Répartir au prorata du SAP</Button>
                    <Tag color={Math.round(reste) === 0 ? "green" : "orange"}>Reste à ventiler : {fmt(reste)} F</Tag>
                </Space>
            </div>
            <Table rowKey="garantieCodeId" size="small" pagination={false} dataSource={garanties}
                locale={{ emptyText: "Aucune garantie évaluée sur ce dossier" }}
                columns={[
                    { title: "Garantie", dataIndex: "garantieNom", render: (v, r) => v || r.garantieCodeId || "—" },
                    { title: "Provision", dataIndex: "provision", align: "right", render: (v) => `${fmt(v)} F` },
                    { title: "Déjà réglé", dataIndex: "regle", align: "right", render: (v) => `${fmt(v)} F` },
                    { title: "SAP", dataIndex: "sap", align: "right", render: (v) => <b style={{ color: (Number(v) || 0) > 0 ? "#b91c1c" : "#16a34a" }}>{fmt(v)} F</b> },
                    {
                        title: "Montant à imputer", key: "m", align: "right", width: 190,
                        render: (_, g) => (
                            <InputNumber style={{ width: "100%" }} min={0} value={montants[g.garantieCodeId]}
                                onChange={(val) => setMontants((m) => ({ ...m, [g.garantieCodeId]: val }))}
                                formatter={(x) => `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                parser={(x) => (x || "").replace(/\s/g, "")} addonAfter="F" />
                        ),
                    },
                ]} />
        </Modal>
    );
};

export default VentilationGarantieModal;
